import { FieldTemplateProps, utils } from "@rjsf/core"
import { Button } from "dash-styled"
import React, { useCallback, useContext } from "react"
import { ReactComponent as TrashIcon } from "icons/lib/trash.svg"
import {
    ToolsContext,
    StylingContext,
    BaseCustomField,
    BaseInvertedCustomField,
    Table,
    TextInput
} from "resha"

function WrapIfAdditional(props: FieldTemplateProps) {
    const {
        classNames,
        disabled,
        label,
        onKeyChange,
        onDropPropertyClick,
        readonly,
        schema
    } = props
    const additional = schema.hasOwnProperty(utils.ADDITIONAL_PROPERTY_FLAG)

    if (!additional) {
        return <div className={classNames}>{props.children}</div>
    }
    return (
        <div className={classNames}>
            <Table
                className="-ml-4"
                style={{
                    width: "calc(100% + 2rem)"
                }}
                getKey={label => label}
                items={[label]}
                rowBgColor=""
                withBorders={false}
                columns={[
                    {
                        headerClassName: "bg-transparent pb-0 pt-0",
                        className: "w-5/12 pl-6",
                        renderHeader: () => null,
                        render: () => (
                            <TextInput
                                singleLine
                                autoFocus
                                enableMarkdown={false}
                                enableCopyTextButton={false}
                                value={label}
                                onValueChange={onKeyChange}
                                className="rounded-lg border border-rhino-300"
                                inputClassName={`rounded-lg bg-transparent py-2 px-3 text-sm leading-5 ${
                                    (label || "").trim() === ""
                                        ? "py-4.5 px-4.5"
                                        : ""
                                }`}
                            />
                        )
                    },
                    {
                        headerClassName: "bg-transparent pb-0 pt-0",
                        className: "w-6/12",
                        renderHeader: () => null,
                        render: () => (
                            <div className="flex-grow flex-shrink">
                                {props.children}
                            </div>
                        )
                    },
                    {
                        headerClassName: "bg-transparent pb-0 pt-0",
                        className: "w-1/12",
                        renderHeader: () => null,
                        render: () => (
                            <Button
                                className="w-auto"
                                variant="text"
                                marginLeft="2"
                                disabled={disabled || readonly}
                                onClick={onDropPropertyClick(label)}
                            >
                                <TrashIcon className="w-5 h-5" />
                            </Button>
                        )
                    }
                ]}
            />
        </div>
    )
}

// https://github.com/rjsf-team/react-jsonschema-form/blob/master/packages/core/src/components/fields/SchemaField.js
export function MappingFieldTemplate<T>(props: FieldTemplateProps<T>) {
    const {
        children,
        rawErrors,
        hidden,
        required,
        displayLabel,
        readonly,
        disabled,
        formData,
        rawHelp,
        schema,
        uiSchema,
        rawDescription,
        onChange
    } = props

    const { skipMissingDataField } = useContext(ToolsContext)
    const { invertLayout } = useContext(StylingContext)
    const handleSelectSuggestion = useCallback(
        (selected: T | undefined) => {
            if (selected !== undefined) {
                onChange(selected)
            }
        },
        [onChange]
    )

    if (skipMissingDataField && formData === undefined) {
        return null
    }

    if (hidden) {
        return <div className="hidden">{children}</div>
    }

    const BaseField = invertLayout ? BaseInvertedCustomField : BaseCustomField
    return (
        <WrapIfAdditional {...props}>
            {displayLabel && props.schema.type !== "null" ? (
                <BaseField
                    label={uiSchema["ui:title"] ?? schema["title"] ?? ""}
                    rawDescription={rawDescription}
                    rawHelp={rawHelp}
                    rawErrors={rawErrors}
                    showRequired={required && !readonly && !disabled}
                    schema={schema}
                    uiSchema={uiSchema}
                    onSelectSuggestion={handleSelectSuggestion}
                >
                    {children}
                </BaseField>
            ) : (
                children
            )}
        </WrapIfAdditional>
    )
}
